import React, { useState, useEffect } from "react";
import axios from "axios";
import { Favorite } from "@material-ui/icons";
import { auth } from "../firebase";
import "./feed.css";
import { getFirestore, doc, getDoc, setDoc } from "firebase/firestore";

const firestoreApiBaseUrl =
    "https://firestore.googleapis.com/v1/projects/scholarship-10c69/databases/(default)/documents";
const pageSize = 1000;

function Feed({ isDarkMode, toggleTheme, id }) {
    const [allScholarships, setAllScholarships] = useState([]);
    const [nextPageToken, setNextPageToken] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hasMoreData, setHasMoreData] = useState(true);
    const [userEmail, setUserEmail] = useState("");
    const [initialFetchDone, setInitialFetchDone] = useState(false);
    const [likedPosts, setLikedPosts] = useState({});
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [expandedScholarship, setExpandedScholarship] = useState(null);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUserEmail(user.email);
            } else {
                setUserEmail("");
            }
        });

        if (userEmail && !initialFetchDone) {
            fetchAllScholarships();
            setInitialFetchDone(true);
        }

        return () => unsubscribe();
    }, [userEmail, initialFetchDone]);

    const fetchAllScholarships = async () => {
        try {
            setLoading(true);
            if (!hasMoreData) {
                return;
            }

            const url = `${firestoreApiBaseUrl}/Scholarship?pageSize=${pageSize}`;

            const response = await axios.get(url);

            const fetchedScholarships = response.data.documents.map((doc) => ({
                id: doc.name.split("/").pop(),
                data: doc.fields,
            }));

            // Filter out expired scholarships
            const nonExpiredScholarships = fetchedScholarships.filter(
                (scholarship) => !scholarship.data.isExpired?.booleanValue
            );

            // Sort scholarships by timestamp
            nonExpiredScholarships.sort((a, b) => {
                const timestampA =
                    new Date(a.data.timestamp?.timestampValue).getTime() || 0;
                const timestampB =
                    new Date(b.data.timestamp?.timestampValue).getTime() || 0;
                return timestampB - timestampA; // Sort in descending order
            });

            // Set the sorted scholarships to state
            setAllScholarships((prevScholarships) => [
                ...prevScholarships,
                ...nonExpiredScholarships,
            ]);

            if (response.data.nextPageToken) {
                setNextPageToken(response.data.nextPageToken);
            } else {
                setNextPageToken(null);
                setHasMoreData(false);
            }
        } catch (error) {
            console.error("Error fetching scholarships: ", error);
        } finally {
            setLoading(false);
        }
    };

    const checkIfBookmarked = async () => {
        try {
            if (!userEmail) {
                console.error("User email not available.");
                return;
            }

            const firestoreDb = getFirestore();
            const bookmarkRef = doc(firestoreDb, "bookmark", userEmail);
            const bookmarkDoc = await getDoc(bookmarkRef);

            if (bookmarkDoc.exists()) {
                const bookmarks = bookmarkDoc.data().bookmarks || [];
                setIsBookmarked(bookmarks.includes(id));
            } else {
                console.error("Bookmark document not found for user:", userEmail);
                // Create the bookmark document
                await setDoc(bookmarkRef, { bookmarks: [] });
                console.log("Bookmark document created for user:", userEmail);
            }
        } catch (error) {
            console.error("Error checking bookmark status:", error);
        }
    };

    useEffect(() => {
        checkIfBookmarked();
    }, [userEmail, id]);

    const handleLike = async (scholarshipId) => {
        try {
            const scholarshipToLike = allScholarships.find((s) => s.id === scholarshipId);

            const requestData = {
                fields: {
                    scholarshipId: { stringValue: scholarshipId },
                    userEmail: { stringValue: userEmail },
                    scholarshipDetails: {
                        mapValue: {
                            fields: {
                                title: { stringValue: scholarshipToLike.data.title?.stringValue || "" },
                                description: { stringValue: scholarshipToLike.data.description?.stringValue || "" },
                                eligibility: { stringValue: scholarshipToLike.data.eligibility?.stringValue || "" },
                                deadline: { stringValue: scholarshipToLike.data.deadline?.stringValue || "" },
                                link: { stringValue: scholarshipToLike.data.link?.stringValue || "" },
                                link1: { stringValue: scholarshipToLike.data.link1?.stringValue || "" }, // Add link field
                                country: { stringValue: scholarshipToLike.data.country?.stringValue || "" },
                                imageUrl: { stringValue: scholarshipToLike.data.imageUrl?.stringValue || "" },
                            },
                        },
                    },
                },
            };

            // Toggle the liked status for the current scholarship
            setLikedPosts((prevLikedPosts) => ({
                ...prevLikedPosts,
                [scholarshipId]: !prevLikedPosts[scholarshipId],
            }));

            await axios.post(
                `${firestoreApiBaseUrl}/bookmark`,
                requestData,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            console.log(`Liked scholarship with ID: ${scholarshipId} for user: ${userEmail}`);
        } catch (error) {
            console.error("Error liking scholarship: ", error);

            if (error.response) {
                console.error("Firestore error data:", error.response.data);
                console.error("Firestore error status:", error.response.status);
                console.error("Firestore error headers:", error.response.headers);
            } else if (error.request) {
                console.error("No response received from Firestore");
            } else {
                console.error("Error setting up the request:", error.message);
            }
        }
    };

    const toggleExpand = (scholarshipId) => {
        setExpandedScholarship((prevExpanded) => (prevExpanded === scholarshipId ? null : scholarshipId));
    };

    return (
        <div className="container mt-5" style={{ backgroundColor: isDarkMode ? '#333' : '#f8f9fa', minHeight: "100vh", overflowX: "hidden" }}> {/* Add overflowX: "hidden" to hide horizontal scrollbar */}
            <h3 className="mb-4 text-center">Scholarships</h3>

            <div className="card-container">
                {allScholarships
                    .sort((a, b) => {
                        // Sort scholarships by timestamp in descending order
                        const timestampA = a.data.timestamp?.stringValue || '';
                        const timestampB = b.data.timestamp?.stringValue || '';
                        return new Date(timestampB) - new Date(timestampA);
                    })
                    .map(({ id, data }) => (
                        <div key={id} className="row mb-4">
                            <div className="col-md">
                                <div className="card mb-4">
                                    {data.imageUrl && (
                                        <img
                                            src={data.imageUrl.stringValue}
                                            alt="Scholarship"
                                            className="card-img-top img-fluid"
                                        />
                                    )}
                                    <div className="card-body" style={{ maxWidth: "100%", textAlign: "left" }}>
                                        <h4 className="card-title mb-0 fw-bold" style={{ overflowWrap: "break-word", maxWidth: "100%" }}>{data.title?.stringValue}</h4>
                                        <p className="card-text" style={{ overflowWrap: "break-word", textAlign: "left" }}>
                                            {expandedScholarship === id ? data.description?.stringValue : `${data.description?.stringValue.slice(0, 100)}...`}
                                        </p>
                                        {expandedScholarship === id && (
                                            <React.Fragment>
                                                <p className="card-text" style={{ overflowWrap: "break-word", textAlign: "left" }}>
                                                    <strong>Eligibility:</strong>{" "}{data.eligibility?.stringValue}
                                                </p>
                                                <p className="card-text" style={{ overflowWrap: "break-word", textAlign: "left" }}>
                                                    <strong>Deadline:</strong>{" "}{data.deadline?.stringValue}
                                                </p>
                                                <p className="card-text" style={{ overflowWrap: "break-word", textAlign: "left" }}>
                                                    <strong>Country:</strong>{" "}{data.country?.stringValue}
                                                </p>
                                                {/* Render the link if it exists */}
                                                {data.link && (
                                                    <div className="card-text" style={{ overflowWrap: "break-word", textAlign: "left" }}>
                                                        <strong>Link:</strong>{" "}
                                                        <a
                                                            href={data.link.stringValue}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {data.link.stringValue}
                                                        </a>
                                                    </div>
                                                )}
                                                {data.link1 && data.link1.stringValue.trim() !== "" && (
                                                    <div className="card-text" style={{ overflowWrap: "break-word", textAlign: "left" }}>
                                                        <strong>Link:</strong>{" "}
                                                        <a
                                                            href={data.link1.stringValue}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {data.link1.stringValue}
                                                        </a>
                                                    </div>
                                                )}
                                            </React.Fragment>
                                        )}
                                        <div className="text-end" style={{ marginTop: "4%" }}> {/* Align to the right */}
                                            <button
                                                className={`read-more-button btn btn-secondary ${window.innerWidth < 576 ? 'btn-sm' : ''}`} // Apply btn-sm class when screen width is less than 576px
                                                onClick={() => toggleExpand(id)}
                                                style={{
                                                    transition: 'opacity 1.0s ease',
                                                    backgroundColor: isDarkMode ? "#333" : "#333",
                                                    color: isDarkMode ? "white" : "white",
                                                }}
                                            >
                                                {expandedScholarship === id ? "Read less" : "Read more"}
                                            </button>
                                        </div>
                                        <div className="text-center" style={{ marginTop: "4%" }}>
                                            <button
                                                className={`like-button btn btn-secondary ${likedPosts[id] ? 'liked' : ''}`}
                                                style={{
                                                    backgroundColor: (likedPosts[id] || isBookmarked) ? 'white' : 'white',
                                                    color: (likedPosts[id] || isBookmarked) ? 'white' : 'whitesmoke'
                                                }}
                                                onClick={() => handleLike(id)}
                                            >
                                                <Favorite style={{ color: (likedPosts[id] || isBookmarked) ? 'red' : 'black' }} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
            </div>
            {hasMoreData && !loading && (
                <button className="btn btn-primary mt-3" onClick={fetchAllScholarships}>
                    Load More
                </button>
            )}
            {loading && <p className="text-center">Loading...</p>}
        </div>

    );
}

export default Feed;

